import { useEffect, useRef, useState } from "react"
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { alpha, type Theme } from "@mui/material/styles"
import { TextFieldElement, type UseFormReturn } from "react-hook-form-mui"
import { skipToken } from "@reduxjs/toolkit/query"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { v4 as uuid } from "uuid"
import equal from "fast-deep-equal"
import {
  RtkHookForm,
  SKIP_FORM_SUBMIT,
} from "@core/components/form/rtkHookForm"
import {
  useRevisionConflict,
  type RevisionSaveAttempt,
} from "../../hooks/useRevisionConflict"
import {
  fromVersionContent,
  type TemplateWriteAccess,
} from "../../hooks/useVersionDraft"
import {
  discardPublicationDraft,
  publicationValues,
  readPublicationDraft,
  retainPublicationDraft,
  publicationsPath,
} from "../../hooks/publicationSessionDraft"
import type { Publication, TemplateVersion } from "../../interface/publications"
import {
  useCreatePublicationMutation,
  useLazyGetPublicationQuery,
  usePatchPublicationMutation,
} from "../../services/publications/publicationsApi"
import {
  useGetTemplatesQuery,
  useGetTemplateVersionQuery,
  useGetTemplateVersionsQuery,
} from "../../services/publications/templatesApi"
import {
  getMissingActiveBindings,
  isBinding,
  mergeBindings,
  toBindingWrite,
} from "../../utils/bindings"
import {
  buildPublicationPatch,
  type PublicationEditable,
} from "../../utils/patches"
import { FeatureErrors } from "../common/featureErrors"
import { RevisionConflictDialog } from "../common/revisionConflictDialog"
import {
  readTemplateReturnContext,
  newTemplateVersionPath,
  templatesPath,
  VersionDraftNavigationGuard,
} from "../templates/templateNavigation"
import { TemplateQueryFeedback } from "../templates/templateQueryFeedback"
import { PublicationBindings } from "./publicationBindings"
import { PublicationImpact } from "./publicationImpact"
import { PublicationPreviewAction } from "./publicationPreviewAction"
import { PublicationContentView } from "./publicationContentView"
import { PostBotEditorLayout } from "../common/postBotPageLayout"
import { readPostBotError } from "../../utils/apiErrors"

export interface PublicationFormProps extends TemplateWriteAccess {
  initial?: Publication
  /** UUID for a new local draft; preserved in Task11's return context. */
  draftId?: string
  onSaved?: (publication: Publication) => void
}
const sectionSx = {
  border: 1,
  borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.14),
  borderRadius: 1,
  p: 2,
  minWidth: 0,
}
export function PublicationForm(props: PublicationFormProps) {
  const [newId] = useState(uuid)
  return (
    <PublicationIdentity
      key={props.initial?.id ?? props.draftId ?? newId}
      {...props}
      draftId={props.draftId ?? newId}
    />
  )
}
function PublicationIdentity(
  props: PublicationFormProps & { draftId: string },
) {
  const [saved, setSaved] = useState<Publication>()
  return (
    <PublicationEditor
      key={saved?.id ?? "initial"}
      {...props}
      initial={saved ?? props.initial}
      onSaved={(value) => {
        if (!props.initial) setSaved(value)
        props.onSaved?.(value)
      }}
    />
  )
}

function PublicationEditor({
  initial,
  draftId,
  readOnly,
  canWrite,
  onSaved,
}: PublicationFormProps & { draftId: string }) {
  const id = initial?.id ?? draftId
  const [seed] = useState(() => {
    const retained = readPublicationDraft(id)
    const baseline = retained?.baseline ?? initial
    const empty: Publication = {
      id,
      name: "",
      templateVersionId: "",
      bindings: {},
      inactiveBindings: {},
      revision: 1,
      createdAt: "",
      updatedAt: "",
    }
    return {
      baseline: baseline ?? empty,
      draft: retained?.draft ?? publicationValues(baseline ?? empty),
      creating: retained ? retained.baseline === null : !initial,
    }
  })
  const [fetchRemote] = useLazyGetPublicationQuery()
  const conflict = useRevisionConflict({
    resourceKey: `publication:${id}`,
    initial: seed.baseline,
    fields: ["name", "templateVersionId", "bindings"],
    bindingFields: ["bindings"],
    fetchRemote: () => fetchRemote(id, false).unwrap(),
  })
  const conflictRef = useRef(conflict)
  conflictRef.current = conflict
  const [context, setContext] = useState<UseFormReturn<PublicationEditable>>()
  const attempt = useRef<RevisionSaveAttempt<Publication> | null>(null)
  const [error, setError] = useState<unknown>()
  const [uncertain, setUncertain] = useState(false)
  const [validation, setValidation] = useState("")
  const [conflictOpen, setConflictOpen] = useState(false)
  const [departure, setDeparture] = useState<{ path: string; state: object }>()
  const [confirmed, setConfirmed] = useState<Publication>()
  const retainOnUnmount = useRef(false)
  const mounted = useRef(false)
  const navigate = useNavigate()
  const location = useLocation()
  const returnContext = readTemplateReturnContext(location.state)
  const offeredId =
    location.state &&
    typeof location.state === "object" &&
    returnContext.returnToPublicationId === id &&
    typeof location.state.savedVersionId === "string"
      ? readTemplateReturnContext({
          sourceTemplateVersionId: location.state.savedVersionId,
        }).sourceTemplateVersionId
      : undefined
  const [dismissedOffer, setDismissedOffer] = useState<string>()
  const [selectedSlot, setSelectedSlot] = useState<{
    versionId: string
    slotId: string
  }>()
  const { state } = conflict
  const draft = state.draft
  const versionQuery = useGetTemplateVersionQuery(
    draft.templateVersionId || skipToken,
  )
  const version =
    !versionQuery.isFetching &&
    !versionQuery.error &&
    versionQuery.currentData?.id === draft.templateVersionId
      ? versionQuery.currentData
      : undefined
  const writable = !readOnly && canWrite !== false
  const editable =
    writable &&
    state.status !== "saving" &&
    state.status !== "conflict" &&
    !departure
  const dirty = !equal(
    publicationValues(state.baseline),
    publicationValues(draft),
  )
  // Presence and write validation are separate: PATCH's diff validates every
  // replacement but omits unchanged legacy reads. CREATE must validate all values.
  const hasRequiredBindings = (bindings: PublicationEditable["bindings"]) =>
    !!version &&
    getMissingActiveBindings(fromVersionContent(version.content), bindings)
      .length === 0 &&
    (!seed.creating || Object.values(bindings).every(isBinding))
  const complete = hasRequiredBindings(draft.bindings)

  useEffect(() => {
    if (!context) return
    const sync = () => {
      const current = conflictRef.current
      current.setDraft({ ...current.state.draft, ...context.getValues() })
    }
    sync()
    const subscription = context.watch(sync)
    return () => subscription.unsubscribe()
  }, [context])
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      if (!retainOnUnmount.current) discardPublicationDraft(id)
    }
  }, [id])
  useEffect(() => {
    if (departure) navigate(departure.path, { state: departure.state })
  }, [departure, navigate])
  useEffect(() => {
    if (!confirmed) return
    // Notify the host only after the saved render has released the dirty guard.
    setConfirmed(undefined)
    onSaved?.(confirmed)
  }, [confirmed, onSaved])

  const leaveForTemplate = (create: boolean) => {
    if (!editable || (!create && !version) || !context) return
    retainPublicationDraft(id, {
      baseline: seed.creating ? null : state.baseline,
      draft: context.getValues(),
    })
    retainOnUnmount.current = true
    setDeparture({
      path: create
        ? `${templatesPath}/new`
        : newTemplateVersionPath(version!.templateId, version!.id),
      state: {
        returnToPublicationId: id,
        ...(!create && version
          ? {
              sourceTemplateId: version.templateId,
              sourceTemplateVersionId: version.id,
            }
          : {}),
      },
    })
  }
  return (
    <>
      <VersionDraftNavigationGuard dirty={dirty && !departure && !confirmed} />
      <PostBotEditorLayout
        preview={
          version ? (
            <PublicationContentView
              version={version}
              bindings={draft.bindings}
              onSelectOccurrence={(occurrence) =>
                setSelectedSlot({
                  versionId: version.id,
                  slotId: occurrence.slotId,
                })
              }
            />
          ) : (
            <Typography
              color="text.secondary"
              sx={{ p: 3, textAlign: "center" }}
            >
              Выберите версию для предпросмотра
            </Typography>
          )
        }
      >
        <RtkHookForm<PublicationEditable>
          mutationHook={
            seed.creating
              ? useCreatePublicationMutation
              : usePatchPublicationMutation
          }
          formContextParams={{ defaultValues: seed.draft }}
          initializeOnce
          resetOnSuccess={false}
          setFormContext={setContext}
          actions={
            seed.creating && !version
              ? []
              : [
                  {
                    text: uncertain
                      ? "Повторить сохранение"
                      : seed.creating
                        ? "Создать публикацию"
                        : "Сохранить публикацию",
                    type: "submit",
                    variant: "contained",
                    disabled:
                      !editable || !complete || (!seed.creating && !dirty),
                  },
                ]
          }
          getSubmitHandlerParams={(values) => {
            if (!editable) return SKIP_FORM_SUBMIT
            const patch = seed.creating
              ? undefined
              : buildPublicationPatch(state.baseline, values)
            if (patch === null) return SKIP_FORM_SUBMIT
            if (
              !version ||
              version.id !== values.templateVersionId ||
              !hasRequiredBindings(values.bindings) ||
              values.name.length < 1 ||
              values.name.length > 200
            ) {
              setValidation(
                "Выберите доступную сохранённую версию, заполните активные значения и название (1–200 символов).",
              )
              return SKIP_FORM_SUBMIT
            }
            setValidation("")
            setError(undefined)
            setUncertain(false)
            attempt.current = conflict.beginSave({
              ...state.baseline,
              ...structuredClone(values),
            })
            if (!attempt.current) return SKIP_FORM_SUBMIT
            return seed.creating
              ? {
                  name: values.name,
                  templateVersionId: values.templateVersionId,
                  bindings: Object.fromEntries(
                    Object.entries(values.bindings).map(([key, value]) => [
                      key,
                      toBindingWrite(value),
                    ]),
                  ),
                }
              : { id, body: patch }
          }}
          afterSubmit={(saved: Publication) => {
            if (!mounted.current) return
            if (
              (!seed.creating && saved.id !== id) ||
              !saved.id ||
              !Number.isSafeInteger(saved.revision) ||
              saved.revision < 1
            )
              throw new Error("Invalid publication response")
            // Never reconstruct bindings from the submitted diff: the response is the baseline.
            if (!seed.creating) attempt.current?.succeed(saved)
            context?.reset(publicationValues(saved))
            discardPublicationDraft(id)
            setConfirmed(saved)
          }}
          onSubmitError={async (failure) => {
            if (!mounted.current) return
            setError(failure)
            const status = readPostBotError(failure).status
            setUncertain(
              seed.creating &&
                !(
                  typeof status === "number" &&
                  status >= 400 &&
                  status < 500 &&
                  status !== 408
                ),
            )
            await attempt.current?.fail(failure)
            if (mounted.current) setConflictOpen(true)
          }}
        >
          <TextFieldElement
            name="name"
            label="Название публикации"
            size="small"
            required
            disabled={!editable}
            validation={{
              maxLength: { value: 200, message: "Не более 200 символов" },
            }}
          />
          <Stack
            component="section"
            aria-label="Шаблон и версия"
            spacing={2}
            sx={sectionSx}
          >
            <Typography variant="subtitle2">Шаблон и версия</Typography>
            <VersionSelection
              creating={seed.creating}
              version={version}
              selectedId={draft.templateVersionId}
              disabled={!editable}
              onSelect={(value) => {
                setSelectedSlot(undefined)
                context?.setValue("templateVersionId", value, {
                  shouldDirty: true,
                })
              }}
            />
            {offeredId && offeredId !== dismissedOffer && (
              <Alert severity="info">
                Сохранена новая версия шаблона. Публикация ещё не переключена.
                <Button
                  disabled={!editable}
                  onClick={() => {
                    setSelectedSlot(undefined)
                    context?.setValue("templateVersionId", offeredId, {
                      shouldDirty: true,
                    })
                    setDismissedOffer(offeredId)
                  }}
                >
                  Выбрать сохранённую версию
                </Button>
                <Button onClick={() => setDismissedOffer(offeredId)}>
                  Не выбирать
                </Button>
              </Alert>
            )}
            <TemplateQueryFeedback
              query={versionQuery}
              empty={
                !!draft.templateVersionId && !version && "Версия недоступна"
              }
            />
            {writable && (
              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                <Button
                  disabled={!editable || !version}
                  onClick={() => leaveForTemplate(false)}
                >
                  Редактирование
                </Button>
                <Button
                  disabled={!editable}
                  onClick={() => leaveForTemplate(true)}
                >
                  Создать шаблон
                </Button>
              </Stack>
            )}
          </Stack>
          {version && (
            <Box
              component="section"
              aria-label="Ссылки публикации"
              sx={sectionSx}
            >
              <PublicationBindings
                key={version.id}
                version={version}
                bindings={draft.bindings}
                readOnly={!editable}
                onCreateVersion={() => leaveForTemplate(false)}
                selected={
                  selectedSlot?.versionId === version.id
                    ? selectedSlot.slotId
                    : undefined
                }
                onSelect={(slotId) =>
                  setSelectedSlot(
                    slotId ? { versionId: version.id, slotId } : undefined,
                  )
                }
                onChange={(replacements) => {
                  if (editable && context)
                    context.setValue(
                      "bindings",
                      mergeBindings(
                        context.getValues("bindings"),
                        replacements,
                      ),
                      { shouldDirty: true },
                    )
                }}
              />
            </Box>
          )}
          {!seed.creating && <PublicationImpact publicationId={id} />}
          {validation && <Alert severity="error">{validation}</Alert>}
          <FeatureErrors
            error={
              error && typeof readPostBotError(error).status !== "number"
                ? {
                    data: {
                      message: "Не удалось сохранить. Проверьте соединение.",
                    },
                  }
                : error
            }
          />
          {uncertain && (
            <Alert severity="warning">
              Публикация могла быть создана. Проверьте список перед повтором{" "}
              <Button
                component={Link}
                to={publicationsPath}
                target="_blank"
                rel="noopener noreferrer"
              >
                Открыть список
              </Button>
            </Alert>
          )}
          <FeatureErrors error={state.fetchError} />
          {state.status === "conflict" && (
            <Button onClick={() => setConflictOpen(true)}>
              Сравнить изменения
            </Button>
          )}
        </RtkHookForm>
        {!seed.creating && (
          <PublicationPreviewAction
            publication={state.baseline}
            dirty={dirty}
            readOnly={readOnly}
            canWrite={canWrite}
            disabled={!editable}
          />
        )}
      </PostBotEditorLayout>
      {state.status === "conflict" && (
        <RevisionConflictDialog
          open={conflictOpen}
          differences={state.differences}
          onClose={() => setConflictOpen(false)}
          onApply={(choices) => {
            const resolved = conflict.resolve(choices)
            if (resolved) context?.reset(publicationValues(resolved.draft))
            setConflictOpen(false)
            setError(undefined)
          }}
        />
      )}
    </>
  )
}

/** Bounded pages. Only a new draft's explicit template choice defaults a version. */
function VersionSelection({
  creating,
  version,
  selectedId,
  disabled,
  onSelect,
}: {
  creating: boolean
  version?: TemplateVersion
  selectedId: string
  disabled: boolean
  onSelect: (id: string) => void
}) {
  const [templateFilter, setTemplateFilter] = useState<string>()
  const templateId = templateFilter ?? version?.templateId ?? ""
  const [templatePage, setTemplatePage] = useState(1)
  const [versionPage, setVersionPage] = useState(1)
  // Only an explicit template change in a new draft creates this one-shot
  // intent. Refetches, saved edits and return handoffs never create one.
  const defaultIntent = useRef<string>()
  const templates = useGetTemplatesQuery({ page: templatePage, limit: 50 })
  const versions = useGetTemplateVersionsQuery(
    templateId ? { templateId, page: versionPage, limit: 50 } : skipToken,
  )
  const templateData = !templates.error ? templates.currentData : undefined
  const versionData = !versions.error ? versions.currentData : undefined
  const templateOptions = templateData?.data ?? []
  const versionOptions =
    versionData?.data.filter((value) => value.templateId === templateId) ?? []
  useEffect(() => {
    if (defaultIntent.current !== templateId || !templateId) return
    if (!creating || selectedId) {
      defaultIntent.current = undefined
      return
    }
    if (
      disabled ||
      versionPage !== 1 ||
      versions.isFetching ||
      !versionData ||
      versionData.page !== 1
    )
      return
    // currentData fences the query arguments; also reject inconsistent parents.
    if (versionData.data.some((value) => value.templateId !== templateId))
      return
    defaultIntent.current = undefined
    const newest = versionData.data[0] // API order: number DESC.
    if (newest) onSelect(newest.id)
  }, [
    creating,
    disabled,
    selectedId,
    templateId,
    versionPage,
    versions.isFetching,
    versionData,
    onSelect,
  ])
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: "minmax(0, 1.4fr) minmax(0, 1fr)",
        },
        gap: 1.5,
      }}
    >
      <Stack spacing={1} sx={{ minWidth: 0 }}>
        <TextField
          size="small"
          select
          label="Шаблон"
          value={templateId}
          disabled={disabled}
          onChange={(event) => {
            const nextTemplate = event.target.value
            setTemplateFilter(nextTemplate)
            setVersionPage(1)
            if (creating && !disabled) {
              defaultIntent.current = nextTemplate || undefined
              onSelect("")
            }
          }}
        >
          <MenuItem value="">Выберите шаблон</MenuItem>
          {templateId &&
            !templateOptions.some((item) => item.id === templateId) && (
              <MenuItem value={templateId}>Шаблон выбранной версии</MenuItem>
            )}
          {templateOptions.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
        <TemplateQueryFeedback
          query={templates}
          empty={templateData?.data.length === 0 && "Шаблонов нет"}
        />
        {(templateData?.totalPages ?? templatePage) > 1 && (
          <Stack direction="row">
            <Button
              disabled={disabled || templatePage <= 1 || templates.isFetching}
              onClick={() => setTemplatePage(templatePage - 1)}
            >
              Предыдущие шаблоны
            </Button>
            <Button
              disabled={
                disabled ||
                !templateData ||
                templatePage >= templateData.totalPages ||
                templates.isFetching
              }
              onClick={() => setTemplatePage(templatePage + 1)}
            >
              Следующие шаблоны
            </Button>
          </Stack>
        )}
      </Stack>
      <Stack spacing={1} sx={{ minWidth: 0 }}>
        <TextField
          size="small"
          select
          label="Версия"
          value={selectedId}
          disabled={
            disabled || !templateId || versions.isFetching || !!versions.error
          }
          onChange={(event) => {
            defaultIntent.current = undefined
            onSelect(event.target.value)
          }}
        >
          <MenuItem value="">Выберите версию</MenuItem>
          {selectedId &&
            !versionOptions.some((item) => item.id === selectedId) && (
              <MenuItem value={selectedId}>
                {version
                  ? `Версия ${version.number} (выбранная)`
                  : "Выбранная версия"}
              </MenuItem>
            )}
          {versionOptions.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              Версия {item.number}
            </MenuItem>
          ))}
        </TextField>
        <TemplateQueryFeedback
          query={versions}
          empty={
            !!templateId &&
            versionData?.data.length === 0 &&
            "Сохранённых версий нет"
          }
        />
        {(versionData?.totalPages ?? versionPage) > 1 && (
          <Stack direction="row">
            <Button
              disabled={disabled || versionPage <= 1 || versions.isFetching}
              onClick={() => setVersionPage(versionPage - 1)}
            >
              Предыдущие версии
            </Button>
            <Button
              disabled={
                disabled ||
                !versionData ||
                versionPage >= versionData.totalPages ||
                versions.isFetching
              }
              onClick={() => setVersionPage(versionPage + 1)}
            >
              Следующие версии
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  )
}
